// El estado del turno del TENS: cómo se lee lo que quedó guardado en el
// dispositivo, cuándo muere y cuál es el turno que se propone (plan del mundo
// TENS, PR2, 2026-10-04).
//
// Vive acá, como dominio puro, para poder probar la expiración y los adapters
// sin montar la pantalla: ahí es donde un test verde suele bendecir lo
// equivocado. HomeTens solo lee, persiste y pinta.
//
// Reglas que este archivo hace cumplir:
//  - El registro es del turno y muere con él, con la MISMA regla que el del
//    enfermero (`turnoExpirado`: el fin del turno más 3 horas contadas desde
//    el ÚLTIMO CAMBIO, no desde el reloj). Antes el TENS usaba la rotativa de
//    Bupa más 3 horas sin mirar el último cambio, que es la familia del bug del
//    2026-08-10 ("borraba el trabajo de todos a la misma hora").
//  - Datos viejos se LEEN con adapters, jamás se migran: el estado de antes
//    guardaba `iniciado`; hoy se llama `creado`. Sin ese adapter, el primer
//    despliegue vaciaba el turno de todos los TENS activos, porque
//    `turnoExpirado` sin fecha de creación responde que el registro expiró.
//  - Un turno que no empezó (todavía sin camas) no se evalúa: no tiene qué
//    expirar.
import { TURNOS, TURNO_24, turnoExpirado } from './registroTurno';
import { calcularTurnoDelDia } from '../utils/calendario';
import { turnoAnclaPostNoche } from './turnoCockpit';
import { sanitizarCama } from './entregaTurno';
import { proximosCuidados } from './tens';
import { riesgoDe, notaRiesgo } from './riesgosRecibidos';
import { SIGNOS_EXAMEN } from './examenFisico';

const esTurno = (t) => TURNOS.includes(t);
const esMs = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * El turno que se le propone a la persona según su calendario, o null si el
 * calendario no dice (sin rotación, día libre, turno diurno que el TENS no
 * registra). Si todavía está dentro de la ventana de cierre de una noche, el
 * turno es el de AYER (misma ancla que usa el cierre del enfermero).
 * @param {object|null} rotacion la rotación del calendario
 * @param {Date} ahora
 * @returns {'Largo'|'Noche'|'24 horas'|null}
 */
export function turnoSugeridoTens(rotacion, ahora = new Date()) {
  if (!rotacion) return null;
  const ancla = turnoAnclaPostNoche(rotacion, ahora);
  const t = ancla ? ancla.turno : calcularTurnoDelDia(ahora, rotacion);
  if (t?.tipo === 'largo') return 'Largo';
  if (t?.tipo === 'noche') return 'Noche';
  if (t?.tipo === '24') return TURNO_24;
  return null;
}

// Dónde vive el turno del TENS en el dispositivo. Una sola fuente: el hub lo
// escribe y la tarjeta del inicio lo lee (lib/tensResumen.js).
export const claveEstadoTens = (userId) => `mipae:tens:${userId}`;

/**
 * Lee el estado guardado y lo deja listo, aplicando la retención.
 * Forma: { turno, creado, updated_at, servicio, camas }.
 *
 * - `creado` es cuándo empezó el turno (al tocar la primera cama). El estado
 *   de antes lo llamaba `iniciado`.
 * - `updated_at` es el último cambio. Lo escriben quienes cambian datos, NUNCA
 *   el efecto que persiste: si no, abrir la app a las 22:00 renovaría la gracia
 *   sin tocar nada.
 * - Sin `creado` (turno sin empezar) no se evalúa la expiración, y el turno que
 *   se propone gana sobre uno elegido en una sesión anterior (que sería de otro
 *   día). Con `creado`, lo elegido jamás se pisa.
 * - Al expirar, el servicio sobrevive (no es un dato del paciente) y las camas
 *   no.
 *
 * @param {object|null} raw lo que había en localStorage, ya parseado
 * @param {{ahora?: number, turnoSugerido?: string|null}} opciones
 */
export function cargarEstadoTens(raw, { ahora = Date.now(), turnoSugerido = null } = {}) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const creado = esMs(r.creado) ? r.creado : esMs(r.iniciado) ? r.iniciado : null;
  const updatedAt = esMs(r.updated_at) ? r.updated_at : creado;
  const servicio = typeof r.servicio === 'string' ? r.servicio : null;
  const camas = r.camas && typeof r.camas === 'object' && !Array.isArray(r.camas) ? r.camas : {};
  const turnoGuardado = esTurno(r.turno) ? r.turno : null;

  if (creado == null) {
    return {
      turno: turnoSugerido || turnoGuardado || 'Largo',
      creado: null,
      updated_at: null,
      servicio,
      camas,
    };
  }

  const turno = turnoGuardado || turnoSugerido || 'Largo';
  if (turnoExpirado({ turno, creado, updated_at: updatedAt }, ahora)) {
    return {
      turno: turnoSugerido || turno,
      creado: null,
      updated_at: null,
      servicio,
      camas: {},
    };
  }
  return { turno, creado, updated_at: updatedAt, servicio, camas };
}

// ── Las camas del turno anterior ─────────────────────────────────────────────
// Son números de cama, no datos del paciente (la tabla turnos_bitacora ya los
// guarda para siempre, solo dígitos). Se recuerdan en el dispositivo para no
// escribirlas de nuevo cada turno.
const MAX_CAMAS_RECIENTES = 40;

export const claveCamasRecientes = (userId) => `mipae:tens:camas-recientes:${userId}`;

/** Quita lo que no sea una cama limpia, las repetidas y las que sobran. */
export function camasLimpias(lista) {
  const vistas = new Set();
  const out = [];
  for (const c of Array.isArray(lista) ? lista : []) {
    const limpia = sanitizarCama(c);
    if (limpia && limpia === String(c) && !vistas.has(limpia)) {
      vistas.add(limpia);
      out.push(limpia);
    }
  }
  return out.sort((a, b) => Number(a) - Number(b)).slice(0, MAX_CAMAS_RECIENTES);
}

export function leerCamasRecientes(userId) {
  try {
    return camasLimpias(JSON.parse(localStorage.getItem(claveCamasRecientes(userId))));
  } catch {
    return [];
  }
}

export function guardarCamasRecientes(userId, camas) {
  const limpias = camasLimpias(camas);
  try {
    if (limpias.length) localStorage.setItem(claveCamasRecientes(userId), JSON.stringify(limpias));
  } catch { /* almacenamiento bloqueado: no se recuerdan, no se rompe nada */ }
  return limpias;
}

// ── Leer y purgar lo guardado en el dispositivo ─────────────────────────────
/**
 * Lee el turno guardado de esta persona y le aplica la retención. Si el turno
 * anterior ya murió, sus números de cama quedan recordados para no escribirlos
 * de nuevo (son ubicaciones, no datos del paciente). Una sola lectura para el
 * hub y para la purga al abrir la app.
 * @returns {{estado: object, murio: boolean}} `murio`: había camas guardadas y
 *   la retención las dio por vencidas.
 */
export function leerEstadoTens(userId, { ahora = Date.now(), turnoSugerido = null } = {}) {
  let raw = null;
  try { raw = JSON.parse(localStorage.getItem(claveEstadoTens(userId))); } catch { raw = null; }
  const estado = cargarEstadoTens(raw, { ahora, turnoSugerido });
  const habian = raw?.camas && typeof raw.camas === 'object' && !Array.isArray(raw.camas) ? Object.keys(raw.camas) : [];
  const murio = habian.length > 0 && !Object.keys(estado.camas).length;
  if (murio) guardarCamasRecientes(userId, habian);
  return { estado, murio };
}

// ¿Hay un turno del TENS abierto en este teléfono? Solo lee: el candado de
// versión (App.jsx) no puede pedir actualizar a mitad de un turno, y el turno
// del TENS vive aquí y no en el registro del enfermero. Un turno vencido no
// cuenta (cargarEstadoTens ya lo deja sin camas).
export function tensConTurnoAbierto(userId, { ahora = Date.now() } = {}) {
  if (!userId) return false;
  let raw = null;
  try { raw = JSON.parse(localStorage.getItem(claveEstadoTens(userId))); } catch { raw = null; }
  if (!raw) return false;
  const { camas } = cargarEstadoTens(raw, { ahora });
  return !!camas && Object.keys(camas).length > 0;
}

/**
 * Borra del dispositivo el turno del TENS que ya murió, aunque nadie vuelva a
 * abrir el hub. Antes el turno vencido (con la evolución, la visita y los
 * registros de cada paciente) seguía guardado hasta que la persona entraba a
 * MiPAE en turno, y la retención promete que se borra al terminar el turno. Corre
 * al abrir la app y al volver a primer plano, junto a la purga del registro del
 * enfermero (App.jsx). Deja el estado como lo dejaría el hub al abrir: el
 * servicio y el turno elegido sobreviven, las camas no.
 * @returns {boolean} si purgó algo
 */
export function purgarTensVencido(userId, { ahora = Date.now() } = {}) {
  if (!userId) return false;
  const { estado, murio } = leerEstadoTens(userId, { ahora });
  if (!murio) return false;
  try {
    localStorage.setItem(claveEstadoTens(userId), JSON.stringify(estado));
  } catch {
    return false;
  }
  return true;
}

// ── Qué toca, por reloj, entre todos los pacientes ───────────────────────────
// Hora local "HH:MM" de un instante (la misma con que se sellan los registros).
export function horaDe(ts = Date.now()) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Marca de tiempo del ÚLTIMO evento de un tipo en la lista de eventos de la
// cama, o null. Los eventos viejos que no traen `ts` cuentan como sin marca.
export function ultimoTs(eventos, tipo) {
  const l = (eventos || []).filter((e) => e.tipo === tipo);
  return l.length ? l[l.length - 1].ts || null : null;
}

// Lee un riesgo re-clasificando al leer, con una guarda más: el puntaje solo cuenta
// si es un NÚMERO. `Number(null)`, `Number('')` o `Number(false)` valen 0, y 0 es un
// puntaje válido de Downton, así que un jsonb adulterado con `puntaje: null` y
// `nivel: 'alto'` se leía como "bajo" (la auditoría de PII del 2026-10-04 lo
// reprodujo). Con un puntaje que no es número se cae al nivel declarado.
function riesgoLimpio(reg, id) {
  const crudo = reg?.riesgos?.[id];
  if (!crudo || typeof crudo !== 'object') return null;
  const limpio = typeof crudo.puntaje === 'number' ? crudo : { ...crudo, puntaje: undefined };
  return riesgoDe({ riesgos: { [id]: limpio } }, id);
}

// El riesgo de LPP de la cama como NIVEL ('alto' | 'moderado' | 'bajo' | null).
// Este es el ÚNICO lector, para que nadie lea `reg.riesgo` a mano. Manda lo que
// declaró quien registra en `reg.riesgos.lpp` (nivel o puntaje, re-clasificado al
// leer: un jsonb adulterado jamás afirma un nivel) y, si no hay nada ahí, el
// nivel LEGADO de `reg.riesgo`, que se sigue leyendo y jamás se migra.
export function nivelLppDe(reg) {
  return riesgoLimpio(reg, 'lpp')?.nivel || reg?.riesgo || null;
}

// El riesgo de caídas de la cama ('alto' | 'bajo' | null). Downton no tiene
// "moderado": un valor que la escala no tiene devuelve null.
export function nivelCaidasDe(reg) {
  return riesgoLimpio(reg, 'caidas')?.nivel || null;
}

// La frase de catálogo del puntaje de Braden, solo cuando hay PUNTAJE (con un
// nivel declarado directo no hay número que decir y la frase de frecuencia de la
// evolución ya lo dice). Es la misma frase que escribe la recepción del
// enfermero, para que el mismo dato se lea igual en las dos capas, PERO sin la cola
// "(recibido del turno anterior)": el TENS no declara de dónde viene el puntaje (se
// lo entregaron o lo midió él) y el documento no puede afirmar un origen que la
// persona no dijo. Por eso se arma con la forma sin origen de la escala.
export function notaLppDe(reg) {
  const r = riesgoLimpio(reg, 'lpp');
  return r && r.puntaje != null ? notaRiesgo('lpp', r.puntaje, 'calculado') : null;
}

// ── Signos vitales: lo que mide el TENS, con las claves y unidades de siempre ──
// Mismas claves y etiquetas que `SIGNOS_EXAMEN` del examen físico (un catálogo,
// dos superficies) menos FiO₂, que depende del soporte del paciente y no se mide
// con el monitor del turno. Todo numérico y re-validado.
//
// NO hay semáforo: qué es "alterado" es un umbral de cada institución y de cada
// paciente, y no le corresponde a MiPAE decidirlo. Y la app NO impide registrar un
// valor real aunque sea gravísimo (una hipoglicemia de 15, una saturación de 49):
// eso es justo lo que hay que documentar. Por eso cada campo tiene DOS límites:
//   · duro: lo humanamente posible. Fuera de eso (o si no es un número) es un error
//     de digitación y no se puede guardar.
//   · blando: lo habitual de escribir. Fuera de eso pero dentro de lo posible se
//     pide revisar y la persona confirma que el valor es correcto; se guarda igual.
const LIMITES_SIGNOS = {
  pas: { duro: [20, 350], blando: [60, 230] },
  pad: { duro: [10, 250], blando: [30, 140] },
  fc: { duro: [10, 350], blando: [30, 220] },
  fr: { duro: [1, 100], blando: [6, 50] },
  spo2: { duro: [20, 100], blando: [70, 100] },
  temp: { duro: [20, 45], blando: [33, 42] },
  hgt: { duro: [1, 1500], blando: [30, 600] },
  dolor_eva: { duro: [0, 10], blando: [0, 10] },
};
export const SIGNOS_TENS = SIGNOS_EXAMEN.filter((c) => c.k !== 'fio2');
// La temperatura admite un decimal; el resto son enteros.
const FORMATO_SIGNO = { temp: /^\d{2}([.,]\d)?$/ };
const FORMATO_ENTERO = /^\d{1,3}$/;
const enRango = (n, [min, max]) => n >= min && n <= max;

/**
 * Evalúa UN campo de la toma.
 * @param {string} k clave de SIGNOS_TENS
 * @param {string|number} crudo lo que escribió la persona
 * @returns {{estado: 'vacio'|'ok'|'revisar'|'imposible', valor: string|null}}
 *   `valor` es el texto normalizado (coma decimal, como se escribe en Chile) y solo
 *   existe en 'ok' y 'revisar'.
 */
export function evaluarSigno(k, crudo) {
  const lim = LIMITES_SIGNOS[k];
  if (!lim) return { estado: 'imposible', valor: null };
  const t = String(crudo ?? '').trim();
  if (!t) return { estado: 'vacio', valor: null };
  if (!(FORMATO_SIGNO[k] || FORMATO_ENTERO).test(t)) return { estado: 'imposible', valor: null };
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n) || !enRango(n, lim.duro)) return { estado: 'imposible', valor: null };
  // Los enteros se guardan como número ("070" queda "70"); la temperatura conserva
  // su decimal con coma, como se escribe en Chile.
  const valor = FORMATO_SIGNO[k] ? t.replace('.', ',') : String(n);
  return { estado: enRango(n, lim.blando) ? 'ok' : 'revisar', valor };
}

/**
 * El valor normalizado de un campo, o null si está vacío o no es posible. Un valor
 * "para revisar" SÍ se devuelve: la persona ya lo confirmó al guardarlo.
 */
export function validarSigno(k, crudo) {
  return evaluarSigno(k, crudo).valor;
}

/**
 * Una toma de signos lista para guardar: solo los campos posibles, o null si no
 * quedó ninguno. Descarta claves desconocidas.
 */
export function normalizarToma(valores = {}) {
  const out = {};
  for (const c of SIGNOS_TENS) {
    const v = validarSigno(c.k, valores?.[c.k]);
    if (v != null) out[c.k] = v;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Lo que la pantalla necesita saber de lo que se está escribiendo: el estado de
 * cada campo, si hay algo imposible (bloquea) o por revisar (pide confirmar) y la
 * toma ya normalizada. Una PAD igual o mayor que la PAS también se pide revisar
 * (en las dos), porque casi siempre es un número al revés.
 */
export function evaluarToma(valores = {}) {
  const campos = {};
  for (const c of SIGNOS_TENS) campos[c.k] = evaluarSigno(c.k, valores?.[c.k]);
  if (campos.pas.valor && campos.pad.valor && Number(campos.pad.valor) >= Number(campos.pas.valor)) {
    if (campos.pas.estado === 'ok') campos.pas = { ...campos.pas, estado: 'revisar' };
    if (campos.pad.estado === 'ok') campos.pad = { ...campos.pad, estado: 'revisar' };
  }
  const estados = Object.values(campos).map((c) => c.estado);
  return {
    campos,
    hayImposible: estados.includes('imposible'),
    hayRevisar: estados.includes('revisar'),
    toma: normalizarToma(valores),
  };
}

/** La etiqueta con que se pide el campo: "FC (lpm)", y el dolor "de 0 a 10". */
export function etiquetaSigno(campo) {
  return campo.k === 'dolor_eva' ? `${campo.label} de 0 a 10` : `${campo.label} (${campo.unidad})`;
}

// La toma como prosa corta, en orden de lectura clínica: PA, FC, FR, T°, SpO₂,
// HGT y dolor. Re-valida al leer (un jsonb adulterado jamás imprime un número
// imposible) y usa las etiquetas y unidades del catálogo ("3/10", sin espacio).
export function prosaToma(toma) {
  const t = normalizarToma(toma);
  if (!t) return '';
  const def = (k) => SIGNOS_TENS.find((c) => c.k === k);
  const unidad = (k) => (def(k).unidad.startsWith('/') ? def(k).unidad : ` ${def(k).unidad}`);
  const con = (k) => `${def(k).label} ${t[k]}${unidad(k)}`;
  const partes = [];
  if (t.pas && t.pad) partes.push(`PA ${t.pas}/${t.pad}${unidad('pas')}`);
  else if (t.pas) partes.push(con('pas'));
  else if (t.pad) partes.push(con('pad'));
  for (const k of ['fc', 'fr', 'temp', 'spo2', 'hgt', 'dolor_eva']) if (t[k]) partes.push(con(k));
  return partes.join(', ');
}

// Lo próximo que toca de UNA cama: la rotación del catéter urinario si el perfil la
// activa (el cambio de posición dejó de agendarse el 2026-10-10, ver RIESGO_LPP).
// `proximosCuidados` ya devuelve solo lo que aplica y ordenado por hora. El reloj del
// turno parte de `creado`.
export function proximosDeCama(reg, { rotacionCupMin = null, creado = null, ahora = Date.now() } = {}) {
  const eventos = reg?.eventos || [];
  return proximosCuidados(
    {
      dispositivos: Object.keys(reg?.visita?.dispositivos || {}),
      ultimaRotacionCup: ultimoTs(eventos, 'rotacion_cup'),
      inicioTurno: creado,
      rotacionCupMin,
    },
    ahora,
  );
}

/**
 * Los próximos cuidados de TODAS las camas, con la cama a la vista, ordenados por
 * hora (lo vencido primero) y cortados en `max`. Sin catéter con reloj no hay
 * nada que agendar y la lista queda vacía: no se inventa una
 * tarea (anti-culpa: nada de "sin cuidados programados").
 * @param {object} camas { [cama]: registro }
 * @returns {Array<{cama: string, id: string, label: string, due: number, vencido: boolean, cadaMin: number}>}
 */
export function proximosEntreCamas(camas = {}, { rotacionCupMin = null, creado = null, ahora = Date.now(), max = 3 } = {}) {
  const out = [];
  for (const [cama, reg] of Object.entries(camas || {})) {
    for (const item of proximosDeCama(reg, { rotacionCupMin, creado, ahora })) out.push({ ...item, cama });
  }
  return out.sort((a, b) => a.due - b.due).slice(0, max);
}
